import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, AlertCircle, Clock, UserCheck, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { getWorkflowStepLabel } from "@/lib/tatHelpers";
import type { Notification } from "@/hooks/useNotifications";

interface NotificationCardProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onViewPatient: (patientId: string) => void;
}

export const NotificationCard = ({ 
  notification, 
  onMarkAsRead,
  onViewPatient 
}: NotificationCardProps) => {
  const getSeverityConfig = () => {
    // Event notifications (MD/PD)
    if (notification.type === 'event') {
      if (notification.event_type === 'MD') {
        return {
          icon: UserCheck,
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary/30',
          label: 'Discharge Initiated',
          variant: 'secondary' as const,
        };
      } else if (notification.event_type === 'PD') {
        return {
          icon: LogOut,
          color: 'text-success',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/30',
          label: 'Discharge Complete',
          variant: 'success' as const,
        };
      }
    }
    
    // TAT breach notifications
    if (notification.alert_type === 'breach') {
      return {
        icon: AlertCircle,
        color: 'text-destructive',
        bgColor: 'bg-destructive/10',
        borderColor: 'border-destructive/20',
        label: 'Breach',
        variant: 'destructive' as const,
      };
    } else if (notification.escalation_level === 2) {
      return {
        icon: AlertTriangle,
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        borderColor: 'border-warning/20',
        label: 'Critical',
        variant: 'warning' as const,
      };
    } else {
      return {
        icon: Clock,
        color: 'text-orange-500',
        bgColor: 'bg-orange-500/10',
        borderColor: 'border-orange-500/20',
        label: 'Warning',
        variant: 'default' as const,
      };
    }
  };

  const severity = getSeverityConfig();
  const Icon = severity.icon;

  const handleCardClick = () => {
    onViewPatient(notification.patient_id);
    onMarkAsRead(notification.id);
  };

  const isEventNotification = notification.type === 'event';

  return (
    <Card 
      className={cn(
        "p-4 cursor-pointer transition-all hover:shadow-md border-l-4",
        severity.borderColor,
        !notification.isRead && "bg-accent/5"
      )}
      onClick={handleCardClick}
    >
      <div className="flex items-start gap-3">
        <div className={cn("p-2 rounded-lg", severity.bgColor)}>
          <Icon className={cn("h-5 w-5", severity.color)} />
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={severity.variant} className="text-xs">
                {severity.label}
              </Badge>
              {!isEventNotification && (
                <span className="text-sm font-medium">
                  {getWorkflowStepLabel(notification.workflow_step)}
                </span>
              )}
              {!notification.isRead && (
                <div className="h-2 w-2 rounded-full bg-primary" />
              )}
            </div>
          </div>

          <div className="space-y-1">
            {isEventNotification ? (
              <p className="text-sm text-foreground">{notification.message}</p>
            ) : (
              <>
                <p className="text-sm text-foreground">
                  {notification.message}
                </p>
                {notification.elapsed_minutes !== undefined && notification.threshold_minutes !== undefined && (
                  <p className="text-sm text-muted-foreground">
                    Elapsed: <span className="font-mono font-medium">{notification.elapsed_minutes} min</span>
                    {' / '}
                    Threshold: <span className="font-mono font-medium">{notification.threshold_minutes} min</span>
                  </p>
                )}
              </>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(notification.sent_at), { addSuffix: true })}
          </p>
        </div>
      </div>
    </Card>
  );
};
